<script setup>
    import { ref, onMounted, computed } from 'vue'
    
    import { useIncentivesStore } from '@/stores/incentives'
    const incentivesStore = useIncentivesStore();

    import { useUserStore } from '@/stores/user'
    const userStore = useUserStore();

    import { useRatingsStore } from '@/stores/ratings'
    const ratingsStore = useRatingsStore();

    import userAvatar from '@/components/userAvatar.vue'
    import PreviewHeader from '@/components/PreviewHeader.vue'
    import LoadingOverlay from '@/components/LoadingOverlay.vue'
    // Fullscreen Loading Overlay für Navigation
    const showFullscreenLoading = ref(false);

    async function handleWidgetNavigation(to) {
        showFullscreenLoading.value = true;
        try {
            await router.push(to);
        } finally {
            showFullscreenLoading.value = false;
        }
    }
    import EmptyState from '@/components/EmptyState.vue'

    function formatEuroValues(value) {
        return (value && !isNaN(value)) ? value.toLocaleString('de-DE'): 0;
    }

    // Helpers for new salesAchievements structure
    function bonusValue(entry) {
        return entry?.salesAchievements?.bonus?.value ?? 0;
    }
    function bonusEntitled(entry) {
        return entry?.salesAchievements?.bonus?.entitled === true;
    }
    function cashBoxValue(entry) {
        return entry?.salesAchievements?.cashBox?.value ?? 0;
    }
    function cashBoxEntitled(entry) {
        return entry?.salesAchievements?.cashBox?.entitled === true;
    }

    async function handleDisplayStatus(display) {
      await userStore.selectDisplayStatus('incentives', display);
    }

    // function formattedSalesAgentName(name) {
    // return name
    //     .toLowerCase() // Konvertiere in Kleinbuchstaben
    //     .replace(/ä/g, 'ae') // Ersetze ä durch ae
    //     .replace(/ö/g, 'oe') // Ersetze ö durch oe
    //     .replace(/ü/g, 'ue') // Ersetze ü durch ue
    //     .replace(/ß/g, 'ss') // Ersetze ß durch ss
    //     .replace(/\s+/g, '-') // Ersetze Leerzeichen durch -
    //     + '.webp'; // Füge die Dateiendung hinzu
    // };

    const handleImageError = (event) => {
        if(!/\/avatars\/dummy\.webp$/.test(event.target.src)) {
            event.target.src = '/avatars/dummy.webp';
        }
    };

    const glitterSpans = ref([])
    function generateRandomSpans() {
        const numberOfSpans = Math.round(Math.random() * 21) + 20 // 20-40 spans
        const spans = []

        for (let i = 0; i < numberOfSpans; i++) {
            const size = Math.random() * 5 + .1 // Random size between 5px and 15px
            const top = Math.random() * -60 + 10 // Random top position between 0% and -100%
            const left = Math.random() * 100 // Random left position between 0% and 100%
            const duration = Math.random() * 10 + 2.5 // Random duration between .5s and 1.5s
            const delay = Math.random() * 5 // Random delay between 0s and 5s

            spans.push({
                size: `${size}px`,
                top: `${top}%`,
                left: `${left}%`,
                animationDuration: `${duration}s`,
                animationDelay: `${delay}s`
            })
        }

        glitterSpans.value = spans
    }

    onMounted(() => {
        generateRandomSpans()
    })

    const podiumImageSrc = (entry) => {
        if (!entry) return '/avatars/dummy.webp';
        const id = entry.idUser;
        if(!id) return '/avatars/dummy.webp';
        // Neuer Proxy-Pfad für Avatare
        return `/avatar/user_${id}.jpg`;
    };

    const podiumImageAlt = (entry) => {
      const display = userStore.currentSelection.incentives.display;
      if (display === 'salesAgents') return 'Profilbild Sales Manager';
      if (display === 'salesTeams') return 'Profilbild Area';
      return 'Profilbild';
    };

    // Disclaimer / Info box state (lean version)
    const showDisclaimer = ref(false);
    const updatedDate = computed(() => {
        const disp = userStore.currentSelection.incentives.display;
        const raw = incentivesStore?.incentive?.[disp]?.dateUpdated || incentivesStore?.incentive?.salesAgents?.dateUpdated;
        return raw ? raw.split(', ')[0] : '—';
    });
    const disclaimerText = computed(() => {
        const disp = userStore.currentSelection.incentives.display;
        const text = incentivesStore?.incentive?.[disp]?.disclaimer;
        return text || 'Kein Hinweis verfügbar.';
    });
    // Collapsed preview (short, safe word boundary, ellipsis)
    const disclaimerPreview = computed(() => {
        const full = disclaimerText.value.trim();
        if(full.length <= 110) return full;
        const slice = full.slice(0,110);
        const lastSpace = slice.lastIndexOf(' ');
        return slice.slice(0,lastSpace>60?lastSpace:110) + '…';
    });
    // Incentive context meta
    const incentiveName = computed(() => {
        const disp = userStore.currentSelection.incentives.display;
        return incentivesStore.incentive?.[disp]?.name || incentivesStore.incentive?.salesAgents?.name || '';
    });
    const incentiveDateRange = computed(() => {
        const disp = userStore.currentSelection.incentives.display;
        const s = incentivesStore.incentive?.[disp]?.dateStart?.split(', ')[0] || incentivesStore.incentive?.salesAgents?.dateStart?.split(', ')[0];
        const e = incentivesStore.incentive?.[disp]?.dateEnd?.split(', ')[0] || incentivesStore.incentive?.salesAgents?.dateEnd?.split(', ')[0];
        return (s && e) ? `${s} – ${e}` : '';
    });
                const incentiveHasMeta = computed(() => !!incentiveName.value || !!incentiveDateRange.value);

        // Footnotes for asterisk markers that adapt on display toggle
        const footnotes = computed(() => {
            const disp = userStore.currentSelection.incentives.display;
            if (disp === 'salesTeams') {
                return [
                    { num: 2, text: 'Teamkasse: wird nur angezeigt, wenn berechtigt.' },
                    { num: 1, text: 'Bonus (AM): wird nur angezeigt, wenn berechtigt.' }
                ];
            }
            return [
                { num: 1, text: 'Bonus: wird nur angezeigt, wenn berechtigt.' }
            ];
        });

import InlineSpinner from '@/components/InlineSpinner.vue';
</script>

<template>
    <div class="incentives-preview-block champions">
        <preview-header
            title="Champions"
            to="/incentives#anchor"
            button-label="Zur Seite"
            :disabled="!incentivesStore.incentiveSales?.salesAgents?.length"
            @click="handleWidgetNavigation({ path: '/incentives', hash: '#anchor' })"
        />
        <div id="champions" class="widget-wrapper champions">
            <LoadingOverlay :show="showFullscreenLoading" label="Daten werden geladen…" spinner-size="lg" />
        <div class="container-wrapper">
            <LoadingOverlay :show="userStore.currentSelection.incentives.isLoading" label="Daten werden geladen!" spinner-size="lg" />
            <div class="container main-content toggle-me">
                <template v-if="incentivesStore.incentiveSales?.salesAgents?.length > 0">  
                    <div class="winners-podium">
                        <div :class="['winner-wrapper', {'highlighted' : entry.highlightedPosition === true}]" v-for="(entry, index) in incentivesStore.topThree[userStore.currentSelection.incentives.display]" :key="entry.id">
                            <div :class="['winner', index === 0 ? '-second' : index === 1 ? '-first' : '-third']">
                                <!-- <div id="glitterWrapper" class="glitter-wrapper" v-if="index === 1">
                                    <span 
                                        v-for="(span, index) in glitterSpans" 
                                        :key="index" 
                                        :style="{ width: span.size, height: span.size, top: span.top, left: span.left, position: 'absolute', animationDuration: span.animationDuration, animationDelay: span.animationDelay }"
                                        class="glitter-span">
                                    </span>
                                </div> -->
                                <img
                                    v-if="userStore.currentSelection.incentives.display==='salesAgents' || userStore.currentSelection.incentives.display==='salesTeams'"
                                    :src="podiumImageSrc(entry)"
                                    :alt="podiumImageAlt(entry)"
                                    @error="handleImageError"
                                />
                                <div class="rookie" v-if="entry.isRookie === 1"><img class="rookie-icon" src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                <!-- <template v-if="formatEuroValues(entry.salesEuroNew) > 0">
                                    <img @error="handleImageError" v-if="userStore.currentSelection.incentives.display==='salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                    <img @error="handleImageError" v-if="userStore.currentSelection.incentives.display==='salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                    <div class="rookie" v-if="entry.isRookie === 1"><img class="rookie-icon" src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                </template>
                                <img v-else :src="'avatars/dummy.webp'" alt="Profilbild"> -->
                            </div>
                            <div class="txt-wrapper">
                                <span v-if="userStore.currentSelection.incentives.display==='salesAgents'">{{ entry.salesAgentName }}</span>
                                <span v-if="userStore.currentSelection.incentives.display==='salesTeams'">{{ entry.salesTeamName }}</span>
                                <span v-if="incentivesStore.incentive.salesAgents.focus==='salesEuroNew'">{{ formatEuroValues(entry.salesEuroNew) }}€</span>
                                <span v-if="incentivesStore.incentive.salesAgents.focus==='salesEuro'">{{ formatEuroValues(entry.salesEuro) }}€</span>
                                <span v-if="entry.rankLastImport !== null" class="rank">{{ entry.rankLastImport }}</span>
                                <!-- <template v-if="formatEuroValues(entry.salesEuroNew)  > 0">
                                    <span v-if="userStore.currentSelection.incentives.display==='salesAgents'">{{ entry.salesAgentName }}</span>
                                    <span v-if="userStore.currentSelection.incentives.display==='salesTeams'">{{ entry.salesTeamName }}</span>
                                    <span v-if="incentivesStore.incentive.salesAgents.focus==='salesEuroNew'">{{ formatEuroValues(entry.salesEuroNew) }}€</span>
                                    <span v-if="incentivesStore.incentive.salesAgents.focus==='salesEuro'">{{ formatEuroValues(entry.salesEuro) }}€</span>
                                    <span v-if="entry.rankLastImport !== null" class="rank">{{ entry.rankLastImport }}</span>
                                </template>
                                <template v-else>
                                    <span v-if="userStore.currentSelection.incentives.display==='salesAgents'">--</span>
                                    <span v-if="userStore.currentSelection.incentives.display==='salesTeams'">--</span>
                                    <span v-if="incentivesStore.incentive.salesAgents.focus==='salesEuroNew'">0€</span>
                                    <span v-if="incentivesStore.incentive.salesAgents.focus==='salesEuro'">0€</span>
                                    <span v-if="entry.rankLastImport !== null" class="rank">{{ entry.rankLastImport }}</span>
                                </template> -->
                            </div>
                        </div>
                    </div>
                    <!-- <div v-if="incentiveHasMeta" class="incentive-meta-bar" role="group" aria-label="Incentive Zeitraum">
                        <div class="meta-left" v-if="incentiveName">{{ incentiveName }}</div>
                        <div class="meta-right" v-if="incentiveDateRange"><span class="range-pill">{{ incentiveDateRange }}</span></div>
                    </div> -->
                    <div class="ranklist-wrapper">
                        <div class="ranklist">
                            <table>
                                <thead>
                                    <tr>
                                        <th>Rang</th>
                                        <th v-if="userStore.currentSelection.incentives.display==='salesAgents'">Sales Manager</th>
                                        <th v-if="userStore.currentSelection.incentives.display==='salesTeams'">Areas</th>
                                        <template v-if="incentivesStore.incentive.salesAgents.focus==='salesEuroNew'">
                                            <th>Neu-Kd.</th>
                                            <th>Umsatz</th>
                                        </template>
                                        <template v-if="incentivesStore.incentive.salesAgents.focus==='salesEuro'">
                                            <th>Umsatz</th>
                                            <th>Neu-Kd.</th>
                                        </template>

                                        <template v-if="userStore.currentSelection.scope ==='salesAgent'">
                                            <th v-if="userStore.currentSelection.incentives.display==='salesAgents'">Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                            <th v-if="userStore.currentSelection.incentives.display==='salesTeams'">Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                        </template>
                                        <template v-else-if="userStore.currentSelection.incentives.display==='salesTeams'">
                                            <th>Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                            <th>AM Bonus</th>
                                        </template>
                                        <template v-else>
                                            <th>Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                        </template>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(entry, index) in incentivesStore.preview" :key="index"
                                        :class="{ 'logged-in': entry.highlightedPosition}">
                                        <td class="position" :class="{'rank-out': entry.rankChangeDirection === 'out', 'rank-new': entry.rankChangeDirection === 'new', 'rank-up': entry.rankChangeCount > 0, 'rank-hold': entry.rankChangeCount === '0', 'rank-down': entry.rankChangeCount < 0}">
                                            <template v-if="entry.rankChangeDirection === 'out'">ST</template>
                                            <template v-else-if="entry.rankLastImport!==null">{{ entry.rankLastImport.toString().padStart(2,'0') }}</template>
                                            <template v-else-if="entry.rankLastImport===null">--</template>
                                            <span class="change-count" v-if="entry.rankChangeDirection === 'new'">neu</span>
                                            <span class="change-count" v-if="entry.rankChangeDirection === 'out'">storno</span>
                                            <span class="change-count" v-if="entry.rankChangeCount !== '0'">{{ entry.rankChangeCount }}</span>
                                            <span class="highlightBorder left" v-if="entry.highlightedPosition === true || entry.rankLastImport === 1 || entry.rankLastImport === 2 || entry.rankLastImport === 3"></span>
                                        </td>
                                        <td>
                                            <!-- {{ entry.idTeam }} -->
                                            <userAvatar
                                              :size="27"
                                              :showSalesAgent="true"
                                              :isSalesAgent="userStore.currentSelection.incentives.display === 'salesAgents'"
                                              :isSalesTeam="userStore.currentSelection.incentives.display === 'salesTeams'"
                                              :idUser="entry.idUser"
                                            />                                            
                                            <div class="marquee" v-if="userStore.currentSelection.incentives.display==='salesAgents'">
                                            <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                            <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                            </div>
                                            <div class="marquee" v-if="userStore.currentSelection.incentives.display==='salesTeams'">
                                                <p>{{ entry.salesTeamName }}</p>
                                                <!-- <p>{{ entry.salesTeamName }}</p> -->
                                            </div>
                                        </td>
                                        <!-- <td class="sm-wrapper isSalesAgent">
                                            <div :class="['rank', (entry.salesAgentStatusCurrent) ? entry.salesAgentStatusCurrent.toLowerCase().replaceAll(' ','-') : '', 'rating-' + entry.rating]">
                                                <p v-if="ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings" class="rating-value">{{ ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings }}</p>
                                                <p v-else class="rating-value">--</p>
                                                <img @error="handleImageError" v-if="userStore.currentSelection.incentives.display==='salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                <img @error="handleImageError" v-if="userStore.currentSelection.incentives.display==='salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                <div class="rookie" v-if="entry.isRookie === 1"><img src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                            </div>
                                            <div class="marquee" v-if="userStore.currentSelection.incentives.display==='salesAgents'">
                                                <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                            </div>
                                            <div class="marquee" v-if="userStore.currentSelection.incentives.display==='salesTeams'">
                                                <p>{{ entry.salesTeamName }}</p>
                                            </div>
                                        </td> -->
                                        <template v-if="incentivesStore.incentive.salesAgents.focus==='salesEuroNew'">
                                            <td>{{ formatEuroValues(entry.salesEuroNew) }}€</td>
                                            <td>{{ formatEuroValues(entry.salesEuro) }}€</td>
                                        </template>
                                        <template v-if="incentivesStore.incentive.salesAgents.focus==='salesEuro'">
                                            <td>{{ formatEuroValues(entry.salesEuro) }}€</td>
                                            <td>{{ formatEuroValues(entry.salesEuroNew) }}€</td>
                                        </template>
                                        <template v-if="userStore.currentSelection.scope ==='salesAgent'">
                                            <!-- Scope: salesAgent viewing own list -->
                                            <td v-if="userStore.currentSelection.incentives.display==='salesTeams'"
                                                :data-cashbox-entitled="cashBoxEntitled(entry)"
                                                :data-cashbox-value="cashBoxValue(entry)"
                                                :class="{ 'achieved' : cashBoxEntitled(entry) && cashBoxValue(entry) > 0, 'not-achieved' : !cashBoxEntitled(entry) && cashBoxValue(entry) > 0 }">
                                                {{ formatEuroValues(cashBoxValue(entry)) }}€
                                            </td>
                                            <td v-if="userStore.currentSelection.incentives.display==='salesAgents'"
                                                :data-bonus-entitled="bonusEntitled(entry)"
                                                :data-bonus-value="bonusValue(entry)"
                                                :class="{ 'achieved' : bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved' : !bonusEntitled(entry) && bonusValue(entry) > 0 }">
                                                {{ formatEuroValues(bonusValue(entry)) }}€
                                            </td>
                                        </template>
                                        <template v-else-if="userStore.currentSelection.incentives.display==='salesTeams'">
                                            <!-- Viewing salesTeams ranking (management or other scope) show both cashBox and bonus -->
                                            <td :data-cashbox-entitled="cashBoxEntitled(entry)" :data-cashbox-value="cashBoxValue(entry)"
                                                :class="{ 'achieved' : cashBoxEntitled(entry) && cashBoxValue(entry) > 0, 'not-achieved' : !cashBoxEntitled(entry) && cashBoxValue(entry) > 0 }">
                                                {{ formatEuroValues(cashBoxValue(entry)) }}€
                                            </td>
                                            <td :data-bonus-entitled="bonusEntitled(entry)" :data-bonus-value="bonusValue(entry)"
                                                :class="{ 'achieved' : bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved' : !bonusEntitled(entry) && bonusValue(entry) > 0 }">
                                                {{ formatEuroValues(bonusValue(entry)) }}€
                                            </td>
                                        </template>
                                        <template v-else>
                                            <!-- Default (salesAgents list for management scope) show bonus -->
                                            <td :data-bonus-entitled="bonusEntitled(entry)" :data-bonus-value="bonusValue(entry)"
                                                :class="{ 'achieved' : bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved' : !bonusEntitled(entry) && bonusValue(entry) > 0 }">
                                                {{ formatEuroValues(bonusValue(entry)) }}€
                                            </td>
                                        </template>
                                    </tr>
                                    <!-- <tr v-if="userStore.currentSelection.incentives.display==='salesAgents' && index === incentivesStore.incentive.salesAgents.maxChampions - 1 && index < incentivesStore.incentiveSales.salesAgents.length - 1"  
                                        class="separator">
                                        <td v-for="i in [1,2,3,4,5]" :key="i">
                                            <span></span>
                                        </td>
                                    </tr> -->
                                    <!-- <tr v-if="userStore.currentSelection.incentives.display==='salesTeams' && index === incentivesStore.incentive.salesTeams.maxChampions - 1 && index < incentivesStore.incentiveSales.salesTeams.length - 1"  
                                        class="separator">
                                        <td v-for="i in [1,2,3,4,5]" :key="i">
                                            <span></span>
                                        </td>
                                    </tr> -->
                                </tbody>
                            </table>
                        </div>
                    </div>
                </template>
                <div v-else>
                    <EmptyState tKey="no_data" size="sm" />
                </div>
            </div>
            <div v-if="incentiveHasMeta && incentivesStore.incentiveSales?.salesAgents?.length > 0" class="incentive-meta-bar container" role="group" aria-label="Incentive Zeitraum">
                <div class="meta-left" v-if="incentiveName">{{ incentiveName }}</div>
                <div class="meta-right" v-if="incentiveDateRange"><span class="range-pill">{{ incentiveDateRange }}</span></div>
            </div>
            <div class="container" v-if="incentivesStore.incentiveSales?.salesAgents?.length > 0">
                <!-- meta bar moved above table, legacy context removed -->
                <div class="update-info compact" role="note" aria-labelledby="incentivesInfoHeading">
                    <h6 id="incentivesInfoHeading" class="info-heading">Stand: <time :datetime="updatedDate">{{ updatedDate }}</time></h6>
                    <div class="disclaimer" role="note" aria-label="Hinweis">
                        <p id="incentiveDisclaimer" class="disclaimer-text">
                            <span v-if="userStore.currentSelection.incentives.display === 'salesAgents'" class="asterisk-mark"><span aria-hidden="true">*</span><sup>1</sup></span>
                            <span v-if="userStore.currentSelection.incentives.display === 'salesTeams'" class="asterisk-mark"><span aria-hidden="true">*</span><sup>2</sup></span>
                            {{ disclaimerText }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div class='wave-wrapper'>
            <div class='wave -one'></div>
            <div class='wave -two'></div>
            <div class='wave -three'></div>
        </div>
        <div class="toggle-wrapper" v-if="incentivesStore.incentiveSales?.salesAgents?.length > 0">
            <div class="toggle-option" @click="handleDisplayStatus('salesAgents')">
                <span class="toggle-label">SalesManager</span>
                <label class="switch">
                    <input type="radio" name="champions-toggle" value="salesAgents" :checked="userStore.currentSelection.incentives.display==='salesAgents'">
                    <span class="custom-radio"></span>
                </label>
            </div>
            <div class="toggle-option" @click="handleDisplayStatus('salesTeams')" >
                <span class="toggle-label">Areas</span>
                <label class="switch">
                    <input type="radio" name="champions-toggle" value="salesTeams" :checked="userStore.currentSelection.incentives.display==='salesTeams'">
                    <span class="custom-radio"></span>
                </label>
            </div>
        </div>
  </div>
</div>
</template>
<style scoped>
/* optional subline styling */
.incentives-preview-block .subline { margin:-.2rem 0 .2rem; }
.incentives-preview-block .context-line { margin:0; font-size:.55rem; opacity:.65; letter-spacing:.35px; }

/* Modern info box */
.update-info { display:flex; flex-direction:column; gap:.45rem; border-radius:12px; background:#1e252c; font-size:.75rem; letter-spacing:.32px; }
.update-info.compact { background:linear-gradient(145deg,#1c2329,#222b33); }
.theme--light .update-info { background:#ffffff; border-color:#d5dde5; }
.info-heading { margin:0; font-size:.6rem; font-weight:600; letter-spacing:.5px; display:flex; gap:.35rem; align-items:baseline; }
.info-heading time { font-weight:600; font-size:.62rem; }
.disclaimer { margin:0; }
.disclaimer summary { list-style:none; cursor:pointer; display:inline-flex; align-items:center; gap:.35rem; font-weight:600; letter-spacing:.5px; font-size:.52rem; padding:.18rem .42rem .2rem; border-radius:999px; background:#1f262c; border:1px solid #2a333c; transition:.2s background,.2s border-color,.2s color; width:fit-content; }
.disclaimer summary:hover { background:#262e35; border-color:#34414c; }
.disclaimer[open] summary { background:#28313a; border-color:#3a4752; }
.chevron { display:inline-block; font-size:.5rem; line-height:1; transform:rotate(0deg); transition:.25s transform; }
.chevron.open { transform:rotate(180deg); }
.theme--light .disclaimer summary { background:#f5f7fa; border-color:#d8e1e8; }
.theme--light .disclaimer[open] summary { background:#edf2f6; border-color:#ccd5dd; }
.disclaimer-text { line-height:1.45; font-size:.6rem; max-width: 100%; }
.disclaimer-preview { margin:0; font-size:.56rem; opacity:.75; line-height:1.35; }
.line-clamp { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; line-clamp:2; }
.asterisk { color:var(--sellwerk-color-secondary,#36b4e8); font-weight:700; }
.theme--light .asterisk { color:var(--sellwerk-color-main,#1677c5); }
.disclaimer summary::-webkit-details-marker { display:none; }
/* Footnotes under disclaimer */
.footnotes { margin-top:.35rem; }
.footnote { margin:0; font-size:.54rem; opacity:.75; line-height:1.35; }
.asterisk-mark { font-weight:700; color:var(--sellwerk-color-secondary,#36b4e8); font-size: .75rem;}
.theme--light .asterisk-mark { color:var(--sellwerk-color-main,#1677c5); }
.asterisk-mark sup { font-size:.65em; vertical-align:super; }
/* Incentive context pill */
.incentive-context { margin:-.2rem 0 .5rem; }
.incentive-context .context-line { margin:0; display:flex; flex-wrap:wrap; gap:.6rem; align-items:center; font-size:.75rem; letter-spacing:.45px; }
.incentive-context .label { background:var(--sellwerk-color-secondary,#36b4e8); color:#fff; padding:.26rem .65rem .3rem; border-radius:10px; font-size:.56rem; font-weight:600; letter-spacing:.5px; }
.theme--light .incentive-context .label { background:var(--sellwerk-color-main,#1677c5); }
.incentive-context .range { opacity:.7; font-weight:500; }
/* New meta bar above table */
.incentive-meta-bar { display:flex; flex-direction: row !important; justify-content:space-between; align-items:center; padding:.5rem .75rem .55rem; background:linear-gradient(145deg,#1c2329,#222b33); border:1px solid #2d3944; border-radius:12px; font-size:.6rem; letter-spacing:.48px; }
.theme--light .incentive-meta-bar { background:#ffffff; border-color:#d5dde5; }
.meta-left { font-weight:600; letter-spacing:.55px; display:flex; align-items:center; gap:.5rem; font-size:.62rem; }
.meta-right { display:flex; align-items:center; }
.range-pill { background:var(--sellwerk-color-secondary,#36b4e8); color:#fff; font-weight:600; letter-spacing:.5px; padding:.28rem .75rem .32rem; border-radius:999px; font-size:.75rem; box-shadow:0 2px 4px -2px rgba(0,0,0,.5); }
.theme--light .range-pill { background:var(--sellwerk-color-main,#1677c5); }
/* Table modern styling */
.ranklist table { width:100%; border-collapse:separate; border-spacing:0 6px; font-size:.75rem; }
.ranklist thead tr { background:transparent; }
.ranklist thead th { text-align:left; font-weight:600; font-size:.75rem; letter-spacing:.5px; padding:.45rem .6rem; color:#cbd5e1; text-transform:uppercase; opacity:.88; }
.theme--light .ranklist thead th { color:#475569; opacity:.9; }
.ranklist tbody tr { background:#1d252c; border:1px solid #2d3540; transition:.18s background,.18s transform; }
.ranklist tbody tr.logged-in { background:linear-gradient(135deg,#22303a,#1d252c); border-color:#364854; box-shadow:0 0 0 1px rgba(255,255,255,.06) inset; }
.ranklist tbody tr:hover { background:#242e36; }
.theme--light .ranklist tbody tr { background:#ffffff; border-color:#d5dde5; }
.theme--light .ranklist tbody tr.logged-in { background:linear-gradient(135deg,#f1f7fb,#ffffff); border-color:#cdd7df; }
.theme--light .ranklist tbody tr:hover { background:#eff4f8; }
.ranklist tbody td { padding:.55rem .6rem; vertical-align:middle; font-size:.75rem; }
.ranklist tbody td.position { font-weight:600; letter-spacing:.48px; color:#e2e8f0; width:56px; font-size:.6rem; }
.theme--light .ranklist tbody td.position { color:#1e293b; }
.ranklist tbody td .change-count { display:inline-block; margin-left:.3rem; font-size:.55rem; font-weight:600; }
/* .ranklist tbody td.achieved { background:rgba(54,180,232,.08); } */
.ranklist tbody td.not-achieved { opacity:.6; }
/* .theme--light .ranklist tbody td.achieved { background:rgba(22,119,197,.10); } */
.ranklist tbody td .marquee p { margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:220px; transition:.2s max-width; }
/* Allow full names on wider viewports */
@media (min-width: 1100px) {
    .ranklist tbody td .marquee p { white-space:normal; overflow:visible; text-overflow:clip; max-width:none; }
}
.ranklist tbody td .rank { font-weight:600; }
.ranklist .highlightBorder.left { position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--sellwerk-color-secondary,#36b4e8); border-radius:3px 0 0 3px; }
.theme--light .ranklist .highlightBorder.left { background:var(--sellwerk-color-main,#1677c5); }
</style>

<!-- <style src="../scss/components/incentivePreview.scss" lang="scss" scoped></style> -->
